import { motion, useInView, Variants } from "motion/react"
import * as React from "react"
import { useRef } from "react"
import { cn } from "src/common/utils"

interface BlurFadeProps {
  children: React.ReactNode
  delay?: number
  duration?: number
  direction?: "up" | "down"
  blur?: string
  inView?: boolean
  className?: string
}

export function BlurFade({
  children,
  delay = 0,
  duration = 0.4,
  direction = "up",
  blur = "6px",
  inView = false,
  className,
}: BlurFadeProps) {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: "0px 0px -50px 0px" })
  const shouldAnimate = inView ? isInView : true

  const yOffset = direction === "up" ? 8 : -8

  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches

  const variants: Variants = {
    hidden: prefersReducedMotion
      ? { opacity: 1 }
      : { opacity: 0, filter: `blur(${blur})`, y: yOffset },
    visible: { opacity: 1, filter: "blur(0px)", y: 0 },
  }

  return (
    <motion.div
      ref={ref}
      className={cn(className)}
      initial="hidden"
      animate={shouldAnimate ? "visible" : "hidden"}
      variants={variants}
      transition={{
        delay,
        duration: prefersReducedMotion ? 0 : duration,
        ease: "easeOut",
      }}
    >
      {children}
    </motion.div>
  )
}
